<lib-modal
  [closeLabel]="'general.close' | translate"
  [visible]="visible()"
  [title]="(createdToken() ? 'pat.dialog.createdTitle' : titleKey()) | translate"
  [contentTemplate]="resolvedTemplate ?? undefined"
  [showButtons]="!createdToken()"
  [saveText]="'pat.dialog.create' | translate"
  [cancelText]="'pat.dialog.cancel' | translate"
  [disabled]="createDisabled"
  (save)="onCreate()"
  (cancel)="onClose()"
  (visibleChange)="onVisibleChange($event)"
></lib-modal>

<ng-template #content>
  @if (createdToken(); as token) {
    <div class="pat-dialog__reveal">
      <p class="pat-dialog__once-hint">
        <mat-icon class="pat-dialog__once-icon" aria-hidden="true">warning_amber</mat-icon>
        {{ 'pat.dialog.onceHint' | translate }}
      </p>
      <div class="pat-dialog__token-field">
        <code class="pat-dialog__token">{{ token }}</code>
        <button
          mat-icon-button
          type="button"
          class="pat-dialog__copy"
          [attr.aria-label]="'pat.dialog.copy' | translate"
          (click)="copyToken()"
        >
          <mat-icon aria-hidden="true">{{ copied() ? 'check' : 'content_copy' }}</mat-icon>
        </button>
      </div>
      @if (copied()) {
        <p class="pat-dialog__copied-hint">{{ 'pat.dialog.copied' | translate }}</p>
      }

      <section class="pat-dialog__quickstart" [attr.aria-label]="'pat.dialog.quickstart.label' | translate">
        <p class="pat-dialog__quickstart-title">
          <mat-icon class="pat-dialog__quickstart-icon" aria-hidden="true">terminal</mat-icon>
          {{ 'pat.dialog.quickstart.title' | translate }}
        </p>
        <p class="pat-dialog__quickstart-hint">{{ 'pat.dialog.quickstart.hint' | translate }}</p>
        <div class="pat-dialog__curl">
          <code class="pat-dialog__curl-code">{{ curlExample() }}</code>
          <button
            mat-icon-button
            type="button"
            class="pat-dialog__copy"
            [attr.aria-label]="'pat.dialog.quickstart.copy' | translate"
            (click)="copyCurl()"
          >
            <mat-icon aria-hidden="true">{{ curlCopied() ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
        @if (curlCopied()) {
          <p class="pat-dialog__copied-hint">{{ 'pat.dialog.quickstart.copied' | translate }}</p>
        }
        <div class="pat-dialog__quickstart-links">
          @if (quickstartUrl) {
            <a [href]="quickstartUrl" target="_blank" rel="noopener noreferrer">
              <mat-icon aria-hidden="true">menu_book</mat-icon>
              {{ 'pat.dialog.quickstart.guideLink' | translate }}
            </a>
          }
          @if (swaggerUrl) {
            <a [href]="swaggerUrl" target="_blank" rel="noopener noreferrer">
              <mat-icon aria-hidden="true">api</mat-icon>
              {{ 'pat.dialog.quickstart.swaggerLink' | translate }}
            </a>
          }
        </div>
      </section>

      <div class="pat-dialog__reveal-actions">
        <button mat-flat-button type="button" (click)="onClose()">{{ 'pat.dialog.done' | translate }}</button>
      </div>
    </div>
  } @else {
    <form [formGroup]="form" class="pat-dialog__form">
      @if (!fixedName()) {
        <mat-form-field appearance="fill" class="pat-dialog__field">
          <mat-label>{{ 'pat.dialog.nameLabel' | translate }}</mat-label>
          <input matInput formControlName="name" maxlength="255" />
        </mat-form-field>
      }

      <mat-form-field appearance="fill" class="pat-dialog__field">
        <mat-label>
          {{ (expiryRequired() ? 'pat.dialog.expiresLabelRequired' : 'pat.dialog.expiresLabel') | translate }}
        </mat-label>
        <input
          matInput
          [matDatepicker]="picker"
          [min]="minExpiryDate"
          [max]="expiryRequired() ? maxExpiryDate : null"
          formControlName="expiresAt"
        />
        <mat-datepicker-toggle matIconSuffix [for]="picker" />
        <mat-datepicker #picker />
        <mat-hint>
          {{ (expiryRequired() ? 'pat.dialog.expiresHintAgent' : 'pat.dialog.expiresHint') | translate }}
        </mat-hint>
        @if (form.controls.expiresAt.hasError('matDatepickerParse')) {
          <mat-error>{{ 'common.errors.invalidDate' | translate }}</mat-error>
        } @else if (form.controls.expiresAt.hasError('matDatepickerMax')) {
          <mat-error>{{ 'pat.dialog.expiresMax' | translate }}</mat-error>
        }
      </mat-form-field>

      <fieldset class="pat-dialog__scopes">
        <legend class="pat-dialog__scopes-legend">{{ 'pat.dialog.scopesLabel' | translate }}</legend>
        <p class="pat-dialog__scopes-hint">{{ 'pat.dialog.scopesHint' | translate }}</p>
        @if (catalogState() === 'loaded') {
          @for (group of scopeGroups(); track group.resource) {
            <div class="pat-dialog__scope-group">
              <span class="pat-dialog__scope-resource">{{ resourceLabel(group.resource) }}</span>
              @if (group.supportsRead || group.supportsWrite) {
                <mat-radio-group
                  class="pat-dialog__scope-levels"
                  [attr.aria-label]="resourceLabel(group.resource)"
                  [value]="levelOf(group.resource)"
                  (change)="setLevel(group.resource, $event.value)"
                >
                  <mat-radio-button value="none" [disabled]="levelOptionDisabled(group.resource, 'none')">
                    {{ 'pat.scope.level.none' | translate }}
                  </mat-radio-button>
                  <mat-radio-button value="read" [disabled]="levelOptionDisabled(group.resource, 'read')">
                    {{ 'pat.scope.level.read' | translate }}
                  </mat-radio-button>
                  @if (group.supportsWrite) {
                    <mat-radio-button value="write" [disabled]="levelOptionDisabled(group.resource, 'write')">
                      {{ 'pat.scope.level.write' | translate }}
                    </mat-radio-button>
                  }
                </mat-radio-group>
              }
              @for (action of group.extraActions; track action) {
                <mat-checkbox
                  class="pat-dialog__scope-extra"
                  [checked]="isExtraSelected(group.resource + ':' + action)"
                  (change)="toggleExtra(group.resource + ':' + action, $event.checked)"
                >
                  {{ extraLabel(group.resource, action) }}
                </mat-checkbox>
              }
            </div>
          }
        } @else if (catalogState() === 'error') {
          <div class="pat-dialog__scopes-error">
            <p class="pat-dialog__scopes-error-text" role="alert">{{ 'pat.dialog.scopesLoadError' | translate }}</p>
            <button mat-stroked-button type="button" (click)="retryCatalog()">{{ 'common.retry' | translate }}</button>
          </div>
        } @else {
          <div class="pat-dialog__scopes-loading" role="status">
            <mat-progress-spinner mode="indeterminate" diameter="24" />
            <span>{{ 'pat.dialog.scopesLoading' | translate }}</span>
          </div>
        }
      </fieldset>
    </form>
  }
</ng-template>
